import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Brain, Activity, Pill, Moon, Sun, Sunset, Heart, Zap,
  ChevronRight, ChevronLeft, CheckCircle2, Shield, ShieldCheck,
  Loader2, Sparkles, Target, Clock, AlertTriangle, Plus, Trash2,
  GripVertical
} from 'lucide-react';

// ============= CONSTANTS =============

const STEPS = [
  { id: 'welcome', label: 'Welcome', icon: Sparkles },
  { id: 'symptoms', label: 'Symptoms', icon: Brain },
  { id: 'medications', label: 'Medications', icon: Pill },
  { id: 'fluctuation', label: 'Fluctuation', icon: Activity },
  { id: 'sleep', label: 'Sleep', icon: Moon },
  { id: 'gut', label: 'Digestion', icon: Heart },
  { id: 'stress', label: 'Stress', icon: Zap },
  { id: 'movement', label: 'Movement', icon: Target },
  { id: 'complete', label: 'Complete', icon: CheckCircle2 },
];

const SYMPTOM_LIST = [
  { id: 'dyskinesia', label: 'Dyskinesia', description: 'Involuntary movements' },
  { id: 'bradykinesia', label: 'Bradykinesia', description: 'Slowness of movement' },
  { id: 'internal_vibration', label: 'Internal Vibration', description: 'Inner trembling sensation' },
  { id: 'tremor', label: 'Tremor', description: 'Visible shaking' },
  { id: 'rigidity', label: 'Rigidity', description: 'Muscle stiffness' },
  { id: 'freezing', label: 'Freezing of Gait', description: 'Feet stuck to floor' },
];

const SEVERITY_OPTIONS = [
  { value: 0, label: 'None', color: 'bg-white/10 text-white/50' },
  { value: 2, label: 'Mild', color: 'bg-emerald-500/20 text-emerald-400' },
  { value: 5, label: 'Moderate', color: 'bg-amber-500/20 text-amber-400' },
  { value: 8, label: 'Severe', color: 'bg-rose-500/20 text-rose-400' },
];

const TIME_OPTIONS = ['05:00', '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', 
  '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00', '22:00'];

const QUALITY_OPTIONS = [
  { value: 'good', label: 'Good', score: 8 },
  { value: 'moderate', label: 'Moderate', score: 5 },
  { value: 'poor', label: 'Poor', score: 2 },
];

const PERIOD_OPTIONS = [
  { value: 'morning', label: 'Morning', icon: Sun },
  { value: 'afternoon', label: 'Afternoon', icon: Sunset },
  { value: 'evening', label: 'Evening', icon: Moon },
];

// ============= SELECTION BUTTON =============

function SelectionButton({ selected, onClick, children, className = '' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`p-4 rounded-xl border text-left transition-all ${
        selected
          ? 'bg-[#C89B7B]/10 border-[#C89B7B]/30 text-white'
          : 'bg-[#0B0F19] border-white/10 text-white/70 hover:border-white/20'
      } ${className}`}
    >
      {children}
    </button>
  );
}

// ============= SEVERITY SELECTOR =============

function SeveritySelector({ value, onChange, label }) {
  return (
    <div className="space-y-2">
      {label && <Label className="text-white/70 text-sm">{label}</Label>}
      <div className="flex gap-2">
        {SEVERITY_OPTIONS.map(opt => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-all ${
              value === opt.value
                ? `${opt.color} border-current`
                : 'bg-[#0B0F19] text-white/50 border-white/10 hover:border-white/20'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ============= STEP: WELCOME =============

function WelcomeStep({ onNext }) {
  return (
    <div className="text-center space-y-6">
      <div className="w-20 h-20 rounded-full bg-[#C89B7B]/20 flex items-center justify-center mx-auto">
        <Brain className="w-10 h-10 text-[#C89B7B]" />
      </div>
      <div>
        <h2 className="text-2xl font-['Outfit'] font-bold text-white mb-2">
          Let's Initialize Your System
        </h2>
        <p className="text-white/50 max-w-md mx-auto">
          We'll capture your unique biological patterns to create a personalized 
          biological and neurological model for you.
        </p>
      </div>
      <div className="space-y-3 text-left max-w-sm mx-auto">
        {[
          'Map your symptom priorities',
          'Configure your medication schedule',
          'Understand your daily fluctuations',
          'Establish your baseline state',
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-3 text-white/70">
            <CheckCircle2 className="w-5 h-5 text-[#C89B7B]" />
            <span className="text-sm">{item}</span>
          </div>
        ))}
      </div>
      <p className="text-white/30 text-xs">Takes about 5-7 minutes</p>
      <Button
        onClick={onNext}
        className="w-full max-w-xs bg-[#C89B7B] hover:bg-[#C89B7B]/90 text-[#0B0F19] font-medium"
        data-testid="start-onboarding"
      >
        Begin Initialization
        <ChevronRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
}

// ============= STEP: SYMPTOMS =============

function SymptomsStep({ data, setData, onNext, onBack }) {
  const [priorities, setPriorities] = useState(data.symptom_priority || {});
  
  const handleSeverityChange = (symptomId, severity) => {
    setPriorities(prev => ({ ...prev, [symptomId]: severity }));
  };
  
  const handleNext = () => {
    setData(prev => ({ ...prev, symptom_priority: priorities }));
    onNext();
  };
  
  const hasSelections = Object.values(priorities).some(v => v > 0);
  
  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-xl font-['Outfit'] font-bold text-white mb-2">
          Your Symptom Profile
        </h2>
        <p className="text-white/50 text-sm">
          Rate the severity of each symptom you typically experience
        </p>
      </div>
      
      <div className="space-y-4">
        {SYMPTOM_LIST.map(symptom => (
          <Card key={symptom.id} className="bg-[#131B2B] border-white/10">
            <CardContent className="py-4 px-5">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-white font-medium">{symptom.label}</p>
                  <p className="text-white/40 text-xs">{symptom.description}</p>
                </div>
              </div>
              <SeveritySelector
                value={priorities[symptom.id] ?? 0}
                onChange={(v) => handleSeverityChange(symptom.id, v)}
              />
            </CardContent>
          </Card>
        ))}
      </div>
      
      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="border-white/20 text-white">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button
          onClick={handleNext}
          disabled={!hasSelections}
          className="flex-1 bg-[#C89B7B] hover:bg-[#C89B7B]/90 text-[#0B0F19] font-medium"
          data-testid="symptoms-next"
        >
          Continue <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

// ============= STEP: MEDICATIONS =============

function MedicationsStep({ data, setData, onNext, onBack }) {
  const [medications, setMedications] = useState(data.medications || [{
    name: 'Levodopa/Carbidopa',
    dose_mg: 100,
    doses_per_day: 4,
    timing: ['07:00', '11:00', '15:00', '19:00'],
    on_latency_minutes: 30,
    on_duration_hours: 3.5,
    wearing_off_pattern: 'predictable',
    dyskinesia_occurs: false,
    dyskinesia_severity: 0,
    food_timing: 'fasting',
  }]);
  
  const updateMed = (index, field, value) => {
    setMedications(prev => prev.map((m, i) => 
      i === index ? { ...m, [field]: value } : m
    ));
  };
  
  const addMedication = () => {
    setMedications(prev => [...prev, {
      name: '',
      dose_mg: 100,
      doses_per_day: 3,
      timing: ['08:00', '14:00', '20:00'],
      on_latency_minutes: 30,
      on_duration_hours: 3,
      wearing_off_pattern: 'predictable',
      dyskinesia_occurs: false,
      dyskinesia_severity: 0,
      food_timing: 'fasting',
    }]);
  };
  
  const removeMed = (index) => {
    if (medications.length > 1) {
      setMedications(prev => prev.filter((_, i) => i !== index));
    }
  };
  
  const handleNext = () => {
    setData(prev => ({ ...prev, medications }));
    onNext();
  };
  
  const isValid = medications.every(m => m.name && m.dose_mg > 0);
  
  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-xl font-['Outfit'] font-bold text-white mb-2">
          Your Medication Schedule
        </h2>
        <p className="text-white/50 text-sm">
          Configure your daily medications and supplements
        </p>
      </div>
      
      {medications.map((med, index) => (
        <Card key={index} className="bg-[#131B2B] border-white/10">
          <CardContent className="py-5 px-5 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-white/50 text-xs uppercase tracking-wider">Medication {index + 1}</p>
              {medications.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeMed(index)}
                  className="h-6 w-6 text-white/30 hover:text-rose-400"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
            
            {/* Name and Dose */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-white/70 text-xs">Medication Name</Label>
                <Input
                  value={med.name}
                  onChange={(e) => updateMed(index, 'name', e.target.value)}
                  placeholder="e.g., Levodopa/Carbidopa"
                  className="bg-[#0B0F19] border-white/10 text-white"
                  data-testid={`med-name-${index}`}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-white/70 text-xs">Dose (mg)</Label>
                <Input
                  type="number"
                  value={med.dose_mg}
                  onChange={(e) => updateMed(index, 'dose_mg', parseInt(e.target.value) || 0)}
                  className="bg-[#0B0F19] border-white/10 text-white"
                  data-testid={`med-dose-${index}`}
                />
              </div>
            </div>
            
            {/* Doses per day */}
            <div className="space-y-1">
              <Label className="text-white/70 text-xs">Doses per day</Label>
              <div className="flex gap-2">
                {[2, 3, 4, 5, 6].map(n => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => {
                      const newTiming = Array(n).fill(0).map((_, i) => {
                        const hour = 7 + Math.floor((i * 14) / (n - 1 || 1));
                        return `${String(hour).padStart(2, '0')}:00`;
                      });
                      updateMed(index, 'doses_per_day', n);
                      updateMed(index, 'timing', newTiming);
                    }}
                    className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-all ${
                      med.doses_per_day === n
                        ? 'bg-[#C89B7B]/20 border-[#C89B7B]/30 text-[#C89B7B]'
                        : 'bg-[#0B0F19] border-white/10 text-white/50 hover:border-white/20'
                    }`}
                  >
                    {n}x
                  </button>
                ))}
              </div>
            </div>
            
            {/* Timing */}
            <div className="space-y-1">
              <Label className="text-white/70 text-xs">Dose Times</Label>
              <div className="flex flex-wrap gap-2">
                {med.timing.map((time, ti) => (
                  <Select
                    key={ti}
                    value={time}
                    onValueChange={(v) => {
                      const newTiming = [...med.timing];
                      newTiming[ti] = v;
                      updateMed(index, 'timing', newTiming);
                    }}
                  >
                    <SelectTrigger className="w-24 h-8 bg-[#0B0F19] border-white/10 text-white text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#131B2B] border-white/10 max-h-48">
                      {TIME_OPTIONS.map(t => (
                        <SelectItem key={t} value={t} className="text-white hover:bg-white/10">
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ))}
              </div>
            </div>
            
            {/* ON characteristics */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-white/70 text-xs">ON starts after (min)</Label>
                <Select
                  value={String(med.on_latency_minutes)}
                  onValueChange={(v) => updateMed(index, 'on_latency_minutes', parseInt(v))}
                >
                  <SelectTrigger className="bg-[#0B0F19] border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#131B2B] border-white/10">
                    {[15, 20, 30, 45, 60, 90].map(m => (
                      <SelectItem key={m} value={String(m)} className="text-white hover:bg-white/10">
                        {m} minutes
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-white/70 text-xs">ON lasts (hours)</Label>
                <Select
                  value={String(med.on_duration_hours)}
                  onValueChange={(v) => updateMed(index, 'on_duration_hours', parseFloat(v))}
                >
                  <SelectTrigger className="bg-[#0B0F19] border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#131B2B] border-white/10">
                    {[2, 2.5, 3, 3.5, 4, 4.5, 5, 6].map(h => (
                      <SelectItem key={h} value={String(h)} className="text-white hover:bg-white/10">
                        {h} hours
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Wearing off pattern */}
            <div className="space-y-1">
              <Label className="text-white/70 text-xs">Wearing-off pattern</Label>
              <div className="flex gap-2">
                {[
                  { value: 'predictable', label: 'Predictable' },
                  { value: 'variable', label: 'Variable' },
                  { value: 'unpredictable', label: 'Unpredictable' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => updateMed(index, 'wearing_off_pattern', opt.value)}
                    className={`flex-1 py-2 px-3 rounded-lg border text-sm transition-all ${
                      med.wearing_off_pattern === opt.value
                        ? 'bg-[#C89B7B]/20 border-[#C89B7B]/30 text-[#C89B7B]'
                        : 'bg-[#0B0F19] border-white/10 text-white/50 hover:border-white/20'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Dyskinesia */}
            <div className="space-y-2">
              <Label className="text-white/70 text-xs">Do you experience dyskinesia with this medication?</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => updateMed(index, 'dyskinesia_occurs', true)}
                  className={`flex-1 py-2 rounded-lg border text-sm transition-all ${
                    med.dyskinesia_occurs
                      ? 'bg-amber-500/20 border-amber-500/30 text-amber-400'
                      : 'bg-[#0B0F19] border-white/10 text-white/50 hover:border-white/20'
                  }`}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => {
                    updateMed(index, 'dyskinesia_occurs', false);
                    updateMed(index, 'dyskinesia_severity', 0);
                  }}
                  className={`flex-1 py-2 rounded-lg border text-sm transition-all ${
                    !med.dyskinesia_occurs
                      ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400'
                      : 'bg-[#0B0F19] border-white/10 text-white/50 hover:border-white/20'
                  }`}
                >
                  No
                </button>
              </div>
              {med.dyskinesia_occurs && (
                <SeveritySelector
                  value={med.dyskinesia_severity}
                  onChange={(v) => updateMed(index, 'dyskinesia_severity', v)}
                  label="Dyskinesia severity"
                />
              )}
            </div>
            
            {/* Food timing */}
            <div className="space-y-1">
              <Label className="text-white/70 text-xs">When do you take this medication?</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'fasting', label: 'Empty stomach' },
                  { value: 'with_food', label: 'With food' },
                  { value: 'before_food', label: 'Before meals' },
                  { value: 'after_food', label: 'After meals' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => updateMed(index, 'food_timing', opt.value)}
                    className={`py-2 px-3 rounded-lg border text-sm transition-all ${
                      med.food_timing === opt.value
                        ? 'bg-[#C89B7B]/20 border-[#C89B7B]/30 text-[#C89B7B]'
                        : 'bg-[#0B0F19] border-white/10 text-white/50 hover:border-white/20'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
      
      <Button
        variant="outline"
        onClick={addMedication}
        className="w-full border-dashed border-white/20 text-white/50 hover:border-white/30 hover:text-white/70"
      >
        <Plus className="w-4 h-4 mr-2" /> Add Another Medication
      </Button>
      
      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="border-white/20 text-white">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button
          onClick={handleNext}
          disabled={!isValid}
          className="flex-1 bg-[#C89B7B] hover:bg-[#C89B7B]/90 text-[#0B0F19] font-medium"
          data-testid="medications-next"
        >
          Continue <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

// ============= STEP: FLUCTUATION =============

function FluctuationStep({ data, setData, onNext, onBack }) {
  const [fluctuation, setFluctuation] = useState(data.daily_fluctuation || {
    best_period: 'morning',
    worst_period: 'evening',
    consistency: 'variable',
  });
  
  const handleNext = () => {
    setData(prev => ({ ...prev, daily_fluctuation: fluctuation }));
    onNext();
  };
  
  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-xl font-['Outfit'] font-bold text-white mb-2">
          Daily Fluctuation Pattern
        </h2>
        <p className="text-white/50 text-sm">
          Help us understand when your symptoms vary throughout the day
        </p>
      </div>
      
      {/* Best period */}
      <Card className="bg-[#131B2B] border-white/10">
        <CardContent className="py-5 px-5">
          <p className="text-white font-medium mb-3">When do you feel best?</p>
          <div className="grid grid-cols-3 gap-2">
            {PERIOD_OPTIONS.map(period => (
              <SelectionButton
                key={period.value}
                selected={fluctuation.best_period === period.value}
                onClick={() => setFluctuation(prev => ({ ...prev, best_period: period.value }))}
                className="text-center"
              >
                <period.icon className={`w-6 h-6 mx-auto mb-2 ${
                  fluctuation.best_period === period.value ? 'text-emerald-400' : 'text-white/40'
                }`} />
                <p className="text-sm">{period.label}</p>
              </SelectionButton>
            ))}
          </div>
        </CardContent>
      </Card>
      
      {/* Worst period */}
      <Card className="bg-[#131B2B] border-white/10">
        <CardContent className="py-5 px-5">
          <p className="text-white font-medium mb-3">When do symptoms worsen?</p>
          <div className="grid grid-cols-3 gap-2">
            {PERIOD_OPTIONS.map(period => (
              <SelectionButton
                key={period.value}
                selected={fluctuation.worst_period === period.value}
                onClick={() => setFluctuation(prev => ({ ...prev, worst_period: period.value }))}
                className="text-center"
              >
                <period.icon className={`w-6 h-6 mx-auto mb-2 ${
                  fluctuation.worst_period === period.value ? 'text-rose-400' : 'text-white/40'
                }`} />
                <p className="text-sm">{period.label}</p>
              </SelectionButton>
            ))}
          </div>
        </CardContent>
      </Card>
      
      {/* Consistency */}
      <Card className="bg-[#131B2B] border-white/10">
        <CardContent className="py-5 px-5">
          <p className="text-white font-medium mb-3">How consistent is your pattern?</p>
          <div className="space-y-2">
            {[
              { value: 'consistent', label: 'Very consistent', desc: 'Same pattern every day' },
              { value: 'variable', label: 'Somewhat variable', desc: 'Changes occasionally' },
              { value: 'unpredictable', label: 'Unpredictable', desc: 'Different each day' },
            ].map(opt => (
              <SelectionButton
                key={opt.value}
                selected={fluctuation.consistency === opt.value}
                onClick={() => setFluctuation(prev => ({ ...prev, consistency: opt.value }))}
              >
                <p className="text-sm font-medium">{opt.label}</p>
                <p className="text-xs text-white/40">{opt.desc}</p>
              </SelectionButton>
            ))}
          </div>
        </CardContent>
      </Card>
      
      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="border-white/20 text-white">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button
          onClick={handleNext}
          className="flex-1 bg-[#C89B7B] hover:bg-[#C89B7B]/90 text-[#0B0F19] font-medium"
          data-testid="fluctuation-next"
        >
          Continue <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

// ============= STEP: SLEEP =============

function SleepStep({ data, setData, onNext, onBack }) {
  const [sleep, setSleep] = useState(data.sleep || {
    quality: 'moderate',
    duration_hours: 7,
    morning_freshness: 'moderate',
  });
  
  const handleNext = () => {
    setData(prev => ({ ...prev, sleep }));
    onNext();
  };
  
  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-xl font-['Outfit'] font-bold text-white mb-2">
          Sleep & Energy Baseline
        </h2>
        <p className="text-white/50 text-sm">
          Tell us about your typical sleep patterns
        </p>
      </div>
      
      {/* Sleep quality */}
      <Card className="bg-[#131B2B] border-white/10">
        <CardContent className="py-5 px-5">
          <p className="text-white font-medium mb-3">How is your sleep quality?</p>
          <div className="grid grid-cols-3 gap-2">
            {QUALITY_OPTIONS.map(opt => (
              <SelectionButton
                key={opt.value}
                selected={sleep.quality === opt.value}
                onClick={() => setSleep(prev => ({ ...prev, quality: opt.value }))}
                className="text-center"
              >
                <p className={`text-sm ${
                  opt.value === 'good' ? 'text-emerald-400' :
                  opt.value === 'moderate' ? 'text-amber-400' : 'text-rose-400'
                }`}>{opt.label}</p>
              </SelectionButton>
            ))}
          </div>
        </CardContent>
      </Card>
      
      {/* Sleep duration */}
      <Card className="bg-[#131B2B] border-white/10">
        <CardContent className="py-5 px-5">
          <p className="text-white font-medium mb-3">Average hours of sleep</p>
          <div className="flex gap-2">
            {[5, 6, 7, 8, 9].map(h => (
              <button
                key={h}
                type="button"
                onClick={() => setSleep(prev => ({ ...prev, duration_hours: h }))}
                className={`flex-1 py-3 rounded-lg border text-lg font-medium transition-all ${
                  sleep.duration_hours === h
                    ? 'bg-[#C89B7B]/20 border-[#C89B7B]/30 text-[#C89B7B]'
                    : 'bg-[#0B0F19] border-white/10 text-white/50 hover:border-white/20'
                }`}
              >
                {h}h
              </button>
            ))}
          </div>
        </CardContent>
      </Card>
      
      {/* Morning freshness */}
      <Card className="bg-[#131B2B] border-white/10">
        <CardContent className="py-5 px-5">
          <p className="text-white font-medium mb-3">How do you feel upon waking?</p>
          <div className="grid grid-cols-3 gap-2">
            {[
              { value: 'good', label: 'Refreshed' },
              { value: 'moderate', label: 'Okay' },
              { value: 'poor', label: 'Tired' },
            ].map(opt => (
              <SelectionButton
                key={opt.value}
                selected={sleep.morning_freshness === opt.value}
                onClick={() => setSleep(prev => ({ ...prev, morning_freshness: opt.value }))}
                className="text-center"
              >
                <p className="text-sm">{opt.label}</p>
              </SelectionButton>
            ))}
          </div>
        </CardContent>
      </Card>
      
      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="border-white/20 text-white">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button
          onClick={handleNext}
          className="flex-1 bg-[#C89B7B] hover:bg-[#C89B7B]/90 text-[#0B0F19] font-medium"
          data-testid="sleep-next"
        >
          Continue <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

// ============= STEP: GUT =============

function GutStep({ data, setData, onNext, onBack }) {
  const [gut, setGut] = useState(data.gut || {
    digestion: 'moderate',
    bloating: 'none',
    bowel_regularity: 'regular',
  });
  
  const handleNext = () => {
    setData(prev => ({ ...prev, gut }));
    onNext();
  };
  
  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-xl font-['Outfit'] font-bold text-white mb-2">
          Gut & Digestion
        </h2>
        <p className="text-white/50 text-sm">
          Gut health significantly impacts how your system feels and responds.
        </p>
      </div>
      
      {/* Digestion quality */}
      <Card className="bg-[#131B2B] border-white/10">
        <CardContent className="py-5 px-5">
          <p className="text-white font-medium mb-3">How is your digestion overall?</p>
          <div className="grid grid-cols-3 gap-2">
            {QUALITY_OPTIONS.map(opt => (
              <SelectionButton
                key={opt.value}
                selected={gut.digestion === opt.value}
                onClick={() => setGut(prev => ({ ...prev, digestion: opt.value }))}
                className="text-center"
              >
                <p className="text-sm">{opt.label}</p>
              </SelectionButton>
            ))}
          </div>
        </CardContent>
      </Card>
      
      {/* Bloating */}
      <Card className="bg-[#131B2B] border-white/10">
        <CardContent className="py-5 px-5">
          <p className="text-white font-medium mb-3">Do you experience bloating?</p>
          <div className="grid grid-cols-4 gap-2">
            {[
              { value: 'none', label: 'Never' },
              { value: 'occasionally', label: 'Sometimes' },
              { value: 'frequently', label: 'Often' },
              { value: 'always', label: 'Always' },
            ].map(opt => (
              <SelectionButton
                key={opt.value}
                selected={gut.bloating === opt.value}
                onClick={() => setGut(prev => ({ ...prev, bloating: opt.value }))}
                className="text-center"
              >
                <p className="text-xs">{opt.label}</p>
              </SelectionButton>
            ))}
          </div>
        </CardContent>
      </Card>
      
      {/* Bowel regularity */}
      <Card className="bg-[#131B2B] border-white/10">
        <CardContent className="py-5 px-5">
          <p className="text-white font-medium mb-3">Bowel movement regularity?</p>
          <div className="grid grid-cols-3 gap-2">
            {[
              { value: 'regular', label: 'Regular' },
              { value: 'constipated', label: 'Constipated' },
              { value: 'variable', label: 'Variable' },
            ].map(opt => (
              <SelectionButton
                key={opt.value}
                selected={gut.bowel_regularity === opt.value}
                onClick={() => setGut(prev => ({ ...prev, bowel_regularity: opt.value }))}
                className="text-center"
              >
                <p className="text-sm">{opt.label}</p>
              </SelectionButton>
            ))}
          </div>
        </CardContent>
      </Card>
      
      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="border-white/20 text-white">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button
          onClick={handleNext}
          className="flex-1 bg-[#C89B7B] hover:bg-[#C89B7B]/90 text-[#0B0F19] font-medium"
          data-testid="gut-next"
        >
          Continue <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

// ============= STEP: STRESS =============

function StressStep({ data, setData, onNext, onBack }) {
  const [stress, setStress] = useState(data.stress || {
    level: 'moderate',
    anxiety: 'occasional',
    triggers: [],
  });
  
  const triggers = ['work', 'family', 'health', 'finances', 'social', 'none'];
  
  const toggleTrigger = (trigger) => {
    setStress(prev => ({
      ...prev,
      triggers: prev.triggers.includes(trigger)
        ? prev.triggers.filter(t => t !== trigger)
        : [...prev.triggers, trigger]
    }));
  };
  
  const handleNext = () => {
    setData(prev => ({ ...prev, stress }));
    onNext();
  };
  
  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-xl font-['Outfit'] font-bold text-white mb-2">
          Stress & Emotional State
        </h2>
        <p className="text-white/50 text-sm">
          Stress can significantly affect symptom severity
        </p>
      </div>
      
      {/* Stress level */}
      <Card className="bg-[#131B2B] border-white/10">
        <CardContent className="py-5 px-5">
          <p className="text-white font-medium mb-3">Current stress level?</p>
          <div className="grid grid-cols-3 gap-2">
            {[
              { value: 'low', label: 'Low' },
              { value: 'moderate', label: 'Moderate' },
              { value: 'high', label: 'High' },
            ].map(opt => (
              <SelectionButton
                key={opt.value}
                selected={stress.level === opt.value}
                onClick={() => setStress(prev => ({ ...prev, level: opt.value }))}
                className="text-center"
              >
                <p className={`text-sm ${
                  opt.value === 'low' ? 'text-emerald-400' :
                  opt.value === 'moderate' ? 'text-amber-400' : 'text-rose-400'
                }`}>{opt.label}</p>
              </SelectionButton>
            ))}
          </div>
        </CardContent>
      </Card>
      
      {/* Anxiety */}
      <Card className="bg-[#131B2B] border-white/10">
        <CardContent className="py-5 px-5">
          <p className="text-white font-medium mb-3">Do you experience anxiety?</p>
          <div className="grid grid-cols-4 gap-2">
            {[
              { value: 'never', label: 'Never' },
              { value: 'occasional', label: 'Sometimes' },
              { value: 'frequent', label: 'Often' },
              { value: 'constant', label: 'Always' },
            ].map(opt => (
              <SelectionButton
                key={opt.value}
                selected={stress.anxiety === opt.value}
                onClick={() => setStress(prev => ({ ...prev, anxiety: opt.value }))}
                className="text-center"
              >
                <p className="text-xs">{opt.label}</p>
              </SelectionButton>
            ))}
          </div>
        </CardContent>
      </Card>
      
      {/* Triggers */}
      <Card className="bg-[#131B2B] border-white/10">
        <CardContent className="py-5 px-5">
          <p className="text-white font-medium mb-3">Main stress triggers (select all that apply)</p>
          <div className="grid grid-cols-3 gap-2">
            {triggers.map(trigger => (
              <SelectionButton
                key={trigger}
                selected={stress.triggers.includes(trigger)}
                onClick={() => toggleTrigger(trigger)}
                className="text-center capitalize"
              >
                <p className="text-sm">{trigger === 'none' ? 'None' : trigger}</p>
              </SelectionButton>
            ))}
          </div>
        </CardContent>
      </Card>
      
      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="border-white/20 text-white">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button
          onClick={handleNext}
          className="flex-1 bg-[#C89B7B] hover:bg-[#C89B7B]/90 text-[#0B0F19] font-medium"
          data-testid="stress-next"
        >
          Continue <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

// ============= STEP: MOVEMENT =============

function MovementStep({ data, setData, onNext, onBack }) {
  const [movement, setMovement] = useState(data.movement || {
    walking_ease: 'moderate',
    stiffness: 'moderate',
    fatigue: 'moderate',
  });
  
  const handleNext = () => {
    setData(prev => ({ ...prev, movement }));
    onNext();
  };
  
  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-xl font-['Outfit'] font-bold text-white mb-2">
          Movement Capacity
        </h2>
        <p className="text-white/50 text-sm">
          Final step - tell us about your movement patterns
        </p>
      </div>
      
      {/* Walking ease */}
      <Card className="bg-[#131B2B] border-white/10">
        <CardContent className="py-5 px-5">
          <p className="text-white font-medium mb-3">How easy is walking for you?</p>
          <div className="grid grid-cols-3 gap-2">
            {[
              { value: 'good', label: 'Easy' },
              { value: 'moderate', label: 'Some difficulty' },
              { value: 'poor', label: 'Challenging' },
            ].map(opt => (
              <SelectionButton
                key={opt.value}
                selected={movement.walking_ease === opt.value}
                onClick={() => setMovement(prev => ({ ...prev, walking_ease: opt.value }))}
                className="text-center"
              >
                <p className="text-sm">{opt.label}</p>
              </SelectionButton>
            ))}
          </div>
        </CardContent>
      </Card>
      
      {/* Stiffness */}
      <Card className="bg-[#131B2B] border-white/10">
        <CardContent className="py-5 px-5">
          <p className="text-white font-medium mb-3">How much stiffness do you experience?</p>
          <div className="grid grid-cols-4 gap-2">
            {[
              { value: 'none', label: 'None' },
              { value: 'mild', label: 'Mild' },
              { value: 'moderate', label: 'Moderate' },
              { value: 'severe', label: 'Severe' },
            ].map(opt => (
              <SelectionButton
                key={opt.value}
                selected={movement.stiffness === opt.value}
                onClick={() => setMovement(prev => ({ ...prev, stiffness: opt.value }))}
                className="text-center"
              >
                <p className="text-xs">{opt.label}</p>
              </SelectionButton>
            ))}
          </div>
        </CardContent>
      </Card>
      
      {/* Fatigue */}
      <Card className="bg-[#131B2B] border-white/10">
        <CardContent className="py-5 px-5">
          <p className="text-white font-medium mb-3">Fatigue during movement?</p>
          <div className="grid grid-cols-4 gap-2">
            {[
              { value: 'none', label: 'None' },
              { value: 'mild', label: 'Mild' },
              { value: 'moderate', label: 'Moderate' },
              { value: 'severe', label: 'Severe' },
            ].map(opt => (
              <SelectionButton
                key={opt.value}
                selected={movement.fatigue === opt.value}
                onClick={() => setMovement(prev => ({ ...prev, fatigue: opt.value }))}
                className="text-center"
              >
                <p className="text-xs">{opt.label}</p>
              </SelectionButton>
            ))}
          </div>
        </CardContent>
      </Card>
      
      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="border-white/20 text-white">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button
          onClick={handleNext}
          className="flex-1 bg-[#C89B7B] hover:bg-[#C89B7B]/90 text-[#0B0F19] font-medium"
          data-testid="movement-next"
        >
          Complete Setup <CheckCircle2 className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

// ============= STEP: COMPLETE =============

function CompleteStep({ result, onDashboard }) {
  const stateColors = {
    stable: { bg: 'bg-emerald-500/20', text: 'text-emerald-400', icon: ShieldCheck },
    watch: { bg: 'bg-amber-500/20', text: 'text-amber-400', icon: Shield },
    at_risk: { bg: 'bg-rose-500/20', text: 'text-rose-400', icon: AlertTriangle },
  };
  
  const sc = stateColors[result?.state || 'stable'];
  const Icon = sc.icon;
  
  return (
    <div className="text-center space-y-6">
      <div className={`w-24 h-24 rounded-full ${sc.bg} flex items-center justify-center mx-auto`}>
        <Icon className={`w-12 h-12 ${sc.text}`} />
      </div>
      
      <div>
        <h2 className="text-2xl font-['Outfit'] font-bold text-white mb-2">
          Your System is Initialized
        </h2>
        <p className="text-white/50">
          We've created your personalized biological model
        </p>
      </div>
      
      {/* Initial state */}
      <Card className="bg-[#131B2B] border-white/10 text-left">
        <CardContent className="py-5 px-6">
          <p className="text-white/50 text-xs uppercase tracking-wider mb-3">Your Initial State</p>
          <div className="flex items-center gap-4">
            <Badge className={`${sc.bg} ${sc.text} border-0 text-lg px-4 py-1`}>
              {(result?.state || 'STABLE').toUpperCase().replace('_', ' ')}
            </Badge>
            <span className="text-white/40 text-sm">{result?.confidence ? `${Math.round(result.confidence * 100)}% confidence` : ''}</span>
          </div>
        </CardContent>
      </Card>
      
      {/* DECODE baseline */}
      <Card className="bg-[#131B2B] border-white/10 text-left">
        <CardContent className="py-5 px-6">
          <p className="text-white/50 text-xs uppercase tracking-wider mb-3">DECODE Baseline</p>
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 bg-[#0B0F19] rounded-lg">
              <p className="text-xs text-[#C89B7B] mb-1">BAS</p>
              <p className="text-lg text-white font-medium">{result?.bas || '—'}/10</p>
            </div>
            <div className="p-3 bg-[#0B0F19] rounded-lg">
              <p className="text-xs text-emerald-400 mb-1">NAS</p>
              <p className="text-lg text-white font-medium">{result?.nas || '—'}/10</p>
            </div>
            <div className="p-3 bg-[#0B0F19] rounded-lg">
              <p className="text-xs text-blue-400 mb-1">TAS</p>
              <p className="text-lg text-white font-medium">{result?.tas || '—'}/10</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* First insight */}
      {result?.insight && (
        <Card className="bg-gradient-to-r from-indigo-500/10 to-transparent border-indigo-500/20 text-left">
          <CardContent className="py-5 px-6">
            <p className="text-indigo-400 text-xs uppercase tracking-wider mb-2">Your First Insight</p>
            <p className="text-white/80 text-sm">{result.insight}</p>
          </CardContent>
        </Card>
      )}
      
      {/* What's next */}
      <div className="space-y-2 text-left max-w-sm mx-auto">
        <p className="text-white/50 text-xs uppercase tracking-wider">What's Been Created</p>
        {[
          'Timeline Today with your medication schedule',
          'Personalized symptom tracking',
          'State monitoring initialized',
          'Insight Engine ready',
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-2 text-white/70">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
            <span className="text-sm">{item}</span>
          </div>
        ))}
      </div>
      
      <Button
        onClick={onDashboard}
        className="w-full max-w-xs bg-[#C89B7B] hover:bg-[#C89B7B]/90 text-[#0B0F19] font-medium"
        data-testid="go-to-dashboard"
      >
        Go to Dashboard
        <ChevronRight className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
}

// ============= MAIN ONBOARDING PAGE =============

export default function OnboardingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [data, setData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  
  const subscriberId = user?.id;
  const progress = ((currentStep + 1) / STEPS.length) * 100;
  
  const handleNext = () => setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
  const handleBack = () => setCurrentStep(prev => Math.max(prev - 1, 0));
  
  const handleComplete = async () => {
    setIsSubmitting(true);
    
    try {
      // Build profile payload
      const profile = {
        symptom_priority: data.symptom_priority || {},
        medication_behavior: {
          medications: data.medications || [],
          on_off_awareness: 'aware',
          wearing_off_pattern: data.medications?.[0]?.wearing_off_pattern || 'predictable',
          typical_on_duration_hours: data.medications?.[0]?.on_duration_hours || 3.5,
        },
        daily_fluctuation: {
          morning: data.daily_fluctuation?.best_period === 'morning' ? 'good' : 
                   data.daily_fluctuation?.worst_period === 'morning' ? 'poor' : 'moderate',
          afternoon: data.daily_fluctuation?.best_period === 'afternoon' ? 'good' :
                     data.daily_fluctuation?.worst_period === 'afternoon' ? 'poor' : 'moderate',
          evening: data.daily_fluctuation?.best_period === 'evening' ? 'good' :
                   data.daily_fluctuation?.worst_period === 'evening' ? 'poor' : 'moderate',
          worst_period: data.daily_fluctuation?.worst_period || 'evening',
          consistency: data.daily_fluctuation?.consistency || 'variable',
        },
        sleep_quality: QUALITY_OPTIONS.find(o => o.value === data.sleep?.quality)?.score || 5,
        sleep_duration_hours: data.sleep?.duration_hours || 7,
        morning_freshness: data.sleep?.morning_freshness || 'moderate',
        gut_status: data.gut?.digestion || 'moderate',
        gut_details: {
          bloating: data.gut?.bloating || 'none',
          bowel_regularity: data.gut?.bowel_regularity || 'regular',
        },
        stress_level: data.stress?.level || 'moderate',
        anxiety: data.stress?.anxiety || 'occasional',
        stress_triggers: data.stress?.triggers || [],
        movement_capacity: {
          walking_ease: data.movement?.walking_ease || 'moderate',
          stiffness: data.movement?.stiffness || 'moderate',
          fatigue: data.movement?.fatigue || 'moderate',
        },
        energy_morning: data.daily_fluctuation?.best_period === 'morning' ? 7 : 5,
        energy_afternoon: data.daily_fluctuation?.best_period === 'afternoon' ? 7 : 5,
        energy_evening: data.daily_fluctuation?.best_period === 'evening' ? 7 : 5,
      };
      
      // Save profile
      const profileRes = await axios.post(`${API}/subscriber-profile/${subscriberId}`, profile);
      
      // Get state
      const stateRes = await axios.get(`${API}/state/${subscriberId}`);
      
      // Get insight
      const insightRes = await axios.get(`${API}/insights/${subscriberId}/daily`).catch(() => ({ data: null }));
      
      // Update user lifecycle
      await axios.patch(`${API}/users/${subscriberId}`, {
        lifecycle_stage: 'predecode_pending',
        onboarding_completed_at: new Date().toISOString(),
      }).catch(() => {});
      
      setResult({
        state: stateRes.data?.state || 'stable',
        confidence: stateRes.data?.confidence || 0.7,
        bas: profileRes.data?.initial_axis_mapping?.bas_estimate,
        nas: profileRes.data?.initial_axis_mapping?.nas_estimate,
        tas: profileRes.data?.initial_axis_mapping?.tas_estimate,
        insight: insightRes.data?.ai_interpretation || insightRes.data?.insights?.[0]?.insight,
      });
      
      handleNext(); // Go to complete step
      toast.success('Profile initialized successfully!');
      
    } catch (error) {
      console.error('Onboarding error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const renderStep = () => {
    const stepId = STEPS[currentStep].id;
    
    switch (stepId) {
      case 'welcome':
        return <WelcomeStep onNext={handleNext} />;
      case 'symptoms':
        return <SymptomsStep data={data} setData={setData} onNext={handleNext} onBack={handleBack} />;
      case 'medications':
        return <MedicationsStep data={data} setData={setData} onNext={handleNext} onBack={handleBack} />;
      case 'fluctuation':
        return <FluctuationStep data={data} setData={setData} onNext={handleNext} onBack={handleBack} />;
      case 'sleep':
        return <SleepStep data={data} setData={setData} onNext={handleNext} onBack={handleBack} />;
      case 'gut':
        return <GutStep data={data} setData={setData} onNext={handleNext} onBack={handleBack} />;
      case 'stress':
        return <StressStep data={data} setData={setData} onNext={handleNext} onBack={handleBack} />;
      case 'movement':
        return (
          <MovementStep 
            data={data} 
            setData={setData} 
            onNext={() => {
              setData(prev => ({ ...prev, movement: data.movement }));
              handleComplete();
            }} 
            onBack={handleBack} 
          />
        );
      case 'complete':
        return <CompleteStep result={result} onDashboard={() => navigate('/dashboard')} />;
      default:
        return null;
    }
  };
  
  if (isSubmitting) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#C89B7B] animate-spin mx-auto mb-4" />
          <p className="text-white/50">Initializing your biological model...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#0B0F19]">
      {/* Header */}
      <div className="border-b border-white/10 bg-[#131B2B]">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <Brain className="w-6 h-6 text-[#C89B7B]" />
              <span className="text-white font-['Outfit'] font-semibold">ReGenflō</span>
            </div>
            {currentStep > 0 && currentStep < STEPS.length - 1 && (
              <span className="text-white/40 text-sm">
                Step {currentStep} of {STEPS.length - 2}
              </span>
            )}
          </div>
          {currentStep > 0 && currentStep < STEPS.length - 1 && (
            <Progress value={progress} className="h-1 bg-white/10" />
          )}
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-8" data-testid="onboarding-page">
        {renderStep()}
      </div>
      
      {/* Step indicators */}
      {currentStep > 0 && currentStep < STEPS.length - 1 && (
        <div className="fixed bottom-0 left-0 right-0 bg-[#131B2B] border-t border-white/10 py-3">
          <div className="max-w-2xl mx-auto px-4">
            <div className="flex items-center justify-center gap-1">
              {STEPS.slice(1, -1).map((step, i) => (
                <div
                  key={step.id}
                  className={`w-2 h-2 rounded-full transition-all ${
                    i + 1 < currentStep ? 'bg-[#C89B7B]' :
                    i + 1 === currentStep ? 'bg-[#C89B7B] w-4' :
                    'bg-white/20'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
